import React from 'react';
import {
  FOLLOWUP_PRIORITY_CHOICES,
  type FollowupPriority,
} from '../../utils/followupPriority';

interface FollowupActionsFormProps {
  needsFollowup: boolean;
  onNeedsFollowupChange: (value: boolean) => void;
  materials: string[];
  onMaterialsChange: (value: string[]) => void;
  actions: string[];
  onActionsChange: (value: string[]) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  priority: FollowupPriority;
  onPriorityChange: (value: FollowupPriority) => void;
}

const MATERIALS = [
  { id: 'pricetags', label: 'Pricetags' },
  { id: 'wobblers', label: 'Wobblers' },
  { id: 'posters', label: 'Posters' },
  { id: 'flyers', label: 'Flyers' },
  { id: 'merch', label: 'Merch' },
];

const ACTIONS = [
  { id: 'meet_decision_maker', label: 'Meet Decision Maker' },
  { id: 'install_pos', label: 'Install POS Material' },
  { id: 'check_competitors', label: 'Check Competitor Activity' },
  { id: 'negotiate_placement', label: 'Negotiate Better Placement' },
  { id: 'photo_documentation', label: 'Photo Documentation' },
];

export const FollowupActionsForm: React.FC<FollowupActionsFormProps> = ({
  needsFollowup,
  onNeedsFollowupChange,
  materials,
  onMaterialsChange,
  actions,
  onActionsChange,
  notes,
  onNotesChange,
  priority,
  onPriorityChange,
}) => {
  const toggleMaterial = (id: string) => {
    if (materials.includes(id)) {
      onMaterialsChange(materials.filter(m => m !== id));
    } else {
      onMaterialsChange([...materials, id]);
    }
  };

  const toggleAction = (id: string) => {
    if (actions.includes(id)) {
      onActionsChange(actions.filter(a => a !== id));
    } else {
      onActionsChange([...actions, id]);
    }
  };

  return (
    <div className="space-y-6">
      {/* Master Checkbox */}
      <label className="flex items-center gap-3 cursor-pointer p-4 bg-yellow-50 border-2 border-yellow-200 rounded-xl hover:bg-yellow-100 transition-colors">
        <input
          type="checkbox"
          checked={needsFollowup}
          onChange={(e) => onNeedsFollowupChange(e.target.checked)}
          className="w-5 h-5 rounded border-gray-300 text-yellow-600 focus:ring-yellow-500"
        />
        <span className="font-black text-lg text-gray-900">Needs Follow-up</span>
      </label>
      
      {needsFollowup && (
        <div className="space-y-6 p-6 bg-gray-50 rounded-xl border-2 border-gray-200 animate-in fade-in slide-in-from-top-4 duration-300">
          {/* Priority Selector */}
          <div>
            <label className="block font-bold text-gray-900 mb-3">Priority</label>
            <div className="flex gap-2">
              {FOLLOWUP_PRIORITY_CHOICES.map((choice) => (
                <button
                  key={choice.value}
                  type="button"
                  onClick={() => onPriorityChange(choice.value)}
                  className={`flex-1 px-2 py-3 rounded-lg font-bold text-white text-xs transition-all ${
                    priority === choice.value
                      ? `${choice.activeClass} ring-4 ring-offset-2 ${choice.ring}`
                      : 'bg-gray-300 text-gray-600 hover:bg-gray-400'
                  }`}
                >
                  {choice.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-600 mt-2">
              Urgent: 2 days • High: 7 days • Medium: 14 days • Low: 30 days
            </p>
          </div>
          
          {/* Materials to Provide */}
          <div>
            <label className="block font-bold text-gray-900 mb-3">Provide Materials</label>
            <div className="grid grid-cols-2 gap-3">
              {MATERIALS.map(mat => (
                <label 
                  key={mat.id} 
                  className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg cursor-pointer hover:border-brand-400 hover:bg-brand-50 transition-all"
                >
                  <input
                    type="checkbox"
                    checked={materials.includes(mat.id)}
                    onChange={() => toggleMaterial(mat.id)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="font-medium text-gray-700">{mat.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Actions Required */}
          <div>
            <label className="block font-bold text-gray-900 mb-3">Actions Required</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ACTIONS.map(action => (
                <label 
                  key={action.id} 
                  className="flex items-center gap-3 p-3 bg-white border-2 border-gray-200 rounded-lg cursor-pointer hover:border-brand-400 hover:bg-brand-50 transition-all"
                >
                  <input
                    type="checkbox"
                    checked={actions.includes(action.id)}
                    onChange={() => toggleAction(action.id)}
                    className="w-4 h-4 rounded border-gray-300 text-brand-600 focus:ring-brand-500"
                  />
                  <span className="font-medium text-gray-700">{action.label}</span>
                </label>
              ))}
            </div>
          </div>
          
          {/* Task hint (follow-up notes → task note in timeline) */}
          <div>
            <label className="block font-bold text-gray-900 mb-1">Task hint</label>
            <p className="text-xs text-gray-500 mb-2">
              What has to be done on the next visit — shown on the task card
            </p>
            <textarea
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              className="w-full p-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none"
              rows={3}
              placeholder="Additional instructions or context for the follow-up visit..."
            />
          </div>
        </div>
      )}
    </div>
  );
};

